import type { DistanceBearingPitch, LocationData } from './types';
// @ts-ignore
import GeographicLib from 'geographiclib';
import { IkeAngle } from '../unit';

const geoidModel = GeographicLib.Geodesic.WGS84;

export interface GeodesicValue {
	/** φ1, latitude of point 1 (degrees) */
	lat1: number;
	/** λ1, longitude of point 1 (degrees) */
	lon1: number;
	/** α1, azimuth of line at point 1 (degrees) */
	azi1: number;
	/** φ2, latitude of point 2 (degrees) */
	lat2: number;
	/** λ2, longitude of point 2 (degrees) */
	lon2: number;
	/** α2, (forward) azimuth of line at point 2 (degrees) */
	azi2: number;
	/** s12, distance from 1 to 2 (meters) */
	s12: number;
	/** σ12, arc length on auxiliary sphere from 1 to 2 (degrees) */
	a12: number;
	/** m12, reduced length of geodesic (meters) */
	m12?: number;
	/** M12, geodesic scale at 2 relative to 1 (dimensionless) */
	M12?: number;
	/** M21, geodesic scale at 1 relative to 2 (dimensionless) */
	M21?: number;
	/** S12, area between geodesic and equator (meters2) */
	S12?: number;
}

/** Utility to provide geospatial operations. */
export const IkeGeodesic = {
	/**
	 * Calculate the absolute distance between two locations.
	 *
	 * @returns Distance in meters.
	 */
	getDistance(source: LocationData, target: LocationData): number {
		// @ts-ignore
		const result: GeodesicValue = geoidModel.Inverse(
			source.latitude,
			source.longitude,
			target.latitude,
			target.longitude
		);
		return Math.abs(result.s12);
	},

	/**
	 * Total geodesic length of an ordered polyline (sum of consecutive segments).
	 *
	 * @returns Distance in meters (0 for fewer than two points). Altitude is ignored.
	 */
	getPolylineDistance(points: ReadonlyArray<{ latitude: number; longitude: number }>): number {
		let total = 0;
		for (let i = 1; i < points.length; i++) {
			total += IkeGeodesic.getDistance(
				{ latitude: points[i - 1].latitude, longitude: points[i - 1].longitude, altitude: 0 },
				{ latitude: points[i].latitude, longitude: points[i].longitude, altitude: 0 }
			);
		}
		return total;
	},

	/** Calculate the vector from one location to another. */
	getVector(source: LocationData, target: LocationData): DistanceBearingPitch {
		// @ts-ignore
		const result: GeodesicValue = geoidModel.Inverse(
			source.latitude,
			source.longitude,
			target.latitude,
			target.longitude
		);
		const distance = result.s12;
		const bearing = IkeAngle.normalize(IkeAngle.toRadians(result.azi1));
		const pitch = 0; // Not implemented until needed

		return { distance, bearing, pitch };
	},

	/**
	 * Move a location by a vector.
	 *
	 * @param source Original location.
	 * @param bearing Bearing in radians.
	 * @param distance Distance in meters.
	 */
	projectLocation(source: LocationData, bearing: number, distance: number): LocationData {
		const bearingDegrees = IkeAngle.toDegrees(bearing);
		// @ts-ignore
		const result: GeodesicValue = geoidModel.Direct(source.latitude, source.longitude, bearingDegrees, distance);

		return {
			altitude: source.altitude,
			longitude: result.lon2,
			latitude: result.lat2,
		};
	},

	/**
	 * Find the center of a set of locations
	 *
	 * @param locations An array of locations to find the center of
	 * @returns The center of the locations
	 */
	findCenter(locations: LocationData[]): LocationData {
		let sumLat = 0;
		let sumLng = 0;
		let subAlt = 0;

		for (let coordinate of locations) {
			sumLat += coordinate.latitude;
			sumLng += coordinate.longitude;
			subAlt += coordinate.altitude;
		}

		return {
			latitude: sumLat / locations.length,
			longitude: sumLng / locations.length,
			altitude: subAlt / locations.length,
		};
	},
};
